import {
  faFile,
  faPencilAlt,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { B2Button } from "@hdb/biome-2";
import { FC, createRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import _ from "lodash";
import ModalComponent from "../../../../../core/components/modal/modal.component";
import TableComponent from "../../../../../core/components/table/table.component";
import HasPermission from "../../../../../core/guards/has-permission";
import SkeletonComponent from "../../../../../shared/custom-spinner/skeleton.component";
import FormBuilderComponent, {
  FormConfig,
} from "../../../../../shared/form-builder/form-builder.component";
import {
  addSLS,
  deleteSLS,
  editSLS,
  getSLS,
  getSLSById,
} from "../../../../../store/slices/sls.slice";
import SearchBarComponent from "../../search-bar/search-bar.component";
import { isValidPhoneNumber } from "src/shared/helpers/phone-no-validity";
import { setValues } from "src/shared/dynamic-form/dynamic-form-util";
import DynamicFormComponent from "src/shared/dynamic-form/dynamic-form-builder.component";
import "../../../admin.scss";

interface SLSListTableComponentProps {
  columns: any;
  data: any[];
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort: any;
  onRefetchData: () => void;
  isLoading: boolean;
  onSearch: (val: string) => void;
}

const SLSListTableComponent: FC<SLSListTableComponentProps> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  onRefetchData,
  isLoading,
  onSearch,
}) => {
  const formRef = createRef();
  const formRefForContacts = createRef();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDetails, setIsDetails] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState<number>(0);
  const [selectedIdToEdit, setSelectedIdToEdit] = useState<number>(0);

  const [initialValues, setInitialValues] = useState<any>({
    // code: "",
    name: "",
    address: "",
  });

  const initialValuesForContactsObjects = {
    motionTime: [
      {
        id: 0,
        pointOfContact: "POC-01",
        phoneNo: "",
      },
    ],
  };

  const [initialValuesForContacts, setInitialValuesForContacts] = useState<any>(
    initialValuesForContactsObjects
  );

  const dispatch = useDispatch();

  const slsDetails = useSelector((state: any) => {
    return state.sls.slsDetails;
  });

  const isLoadingSlsDetails = useSelector((state: any) => {
    return state.sls.isLoadingSlsDetails;
  });

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsDetails(false);
    setIsDelete(false);
    setIsEdit(false);

    setInitialValues({
      // code: "",
      name: "",
      address: "",
    });

    setBlockContactsValues(initialValuesForContactsObjects);
  };

  const detailsAction = (id: number) => {
    setIsDetails(true);
    setIsModalOpen(true);
    setIsDelete(false);
    setIsEdit(false);

    dispatch(getSLSById({ id }));
  };

  const deleteAction = (id: number) => {
    setIsDetails(false);
    setIsModalOpen(true);
    setIsDelete(true);
    setIsEdit(false);
    setSelectedIdToDelete(id);
  };

  const handleDelete = () => {
    dispatch(deleteSLS({ id: selectedIdToDelete })).then(() => {
      onRefetchData();
      handleCloseModal();
    });
  };

  interface ContactInfo {
    id: number;
    pointOfContact: string;
    phoneNo: string;
  }

  useEffect(() => {
    if (!slsDetails || !Object.keys(slsDetails).length) return;

    setInitialValues({
      // code: slsDetails.code,
      name: slsDetails.name,
      address: slsDetails?.address || "",
    });

    // Handle multiple contacts from motionTime
    const newValuesForContactsObjects = slsDetails?.contactDetail?.length
      ? {
        motionTime: slsDetails.contactDetail
          .map(
            (
              item: { id: number; pointOfContact: string; phoneNo: string },
              index: number
            ) => ({
              id: item.id || 0,
              pointOfContact:
                item.pointOfContact ||
                `POC-${index + 1 < 10 ? "0" : ""}${index + 1}` ||
                "",
              phoneNo: item.phoneNo?.startsWith("+65")
                ? item.phoneNo.substring(3)
                : item.phoneNo || "",
            })
          )
          .sort((a: ContactInfo, b: ContactInfo) =>
            a.pointOfContact.localeCompare(b.pointOfContact)
          ),
      }
      : initialValuesForContactsObjects;

    setBlockContactsValues(newValuesForContactsObjects);
  }, [slsDetails]);

  const isValidSinglePhoneNumber = (phoneNo: string) => {
    return !!phoneNo && phoneNo.length > 0 && isValidPhoneNumber(phoneNo);
  };

  const checkForDuplicatePhoneNumbers = (motionTimeEntries: any[]) => {
    if (!motionTimeEntries || motionTimeEntries.length === 0) {
      return true;
    }

    // Get all phone numbers from entries (filter out empty ones)
    const phoneNumbers = motionTimeEntries
      .map((entry: any) => entry.phoneNo?.trim())
      .filter((phoneNo: string) => phoneNo && phoneNo.length > 0);

    // Check if any phone number appears more than once
    const phoneNumberCount = new Map();

    for (const phoneNo of phoneNumbers) {
      const count = phoneNumberCount.get(phoneNo) || 0;
      phoneNumberCount.set(phoneNo, count + 1);

      // If we find a duplicate, return false immediately
      if (count >= 1) {
        return false;
      }
    }

    return true;
  };

  const onSubmit = () => {
    const form: any = formRef.current;

    if (form) {
      const { name, address } = form.values;

      const formContacts: any = formRefForContacts.current;
      const { motionTime } = formContacts.values;
      const motionTimeEntries = motionTime || [];
      form.handleSubmit();

      if (formContacts) {
        formContacts.handleSubmit();

        // Validate each phone number and POC
        // Validate all entries
        const isValid = motionTimeEntries.every((entry: any) => {
          const { pointOfContact, phoneNo } = entry;
          return pointOfContact && isValidSinglePhoneNumber(phoneNo);
        });

        if (!isValid) {
          console.error("Validation failed for one or more contacts");
          return;
        }

        // Check for duplicate phone numbers
        const hasNoDuplicates =
          checkForDuplicatePhoneNumbers(motionTimeEntries);

        if (!hasNoDuplicates) {
          console.error("Duplicate phone numbers found");
          return;
        }
      }

      // check for name validation
      if (
        !name ||
        !address ||
        name?.length < 2 ||
        name?.length > 50 ||
        address < 2 ||
        address > 1000
      ) {
        return;
      }

      const payload = {
        name: name,
        address: address,
        contactDetail: motionTimeEntries.map((entry: any) => ({
          id: entry.id || 0, // Use existing id or default to 0 if not provided
          pointOfContact: entry.pointOfContact,
          phoneNo: "+65" + entry.phoneNo,
        })),
      };

      if (isEdit) {
        dispatch(editSLS({ payload: payload, id: selectedIdToEdit })).then(
          () => {
            handleCloseModal();
            onRefetchData();
          }
        );
      } else if (name && name?.length >= 2) {
        dispatch(addSLS({ payload: payload })).then(() => {
          handleCloseModal();
          onRefetchData();
        });
      }
    }
  };

  const editAction = (id: number) => {
    dispatch(getSLSById({ id }));
    setSelectedIdToEdit(id);

    setIsDetails(false);
    setIsModalOpen(true);
    setIsEdit(true);
    setIsDelete(false);
    setSelectedIdToEdit(id);
  };

  let modalActionConfig: any;
  if (isDelete) {
    modalActionConfig = [
      {
        label: "Yes",
        event: handleDelete,
        variant: "primary",
      },
    ];
  } else if (isDetails) {
    modalActionConfig = [];
  } else {
    modalActionConfig = [
      {
        label: "Save",
        event: onSubmit,
        variant: "primary",
      },
    ];
  }

  const actionConfig: any = [
    {
      label: "Edit",
      actionProperty: "id",
      event: editAction,
      variant: "secondary",
      className: "mx-2 my-1",
      moduleName: "Supplier List",
      actionName: "Edit-Supplier List",
      icon: faPencilAlt,
    },
    {
      label: "View",
      actionProperty: "id",
      event: detailsAction,
      variant: "info",
      className: "mx-2 my-1",
      moduleName: "Supplier List",
      actionName: "View-Supplier List",
      icon: faFile,
    },
    {
      label: "Delete",
      actionProperty: "id",
      event: deleteAction,
      variant: "danger",
      className: "mx-2 my-1",
      moduleName: "Supplier List",
      actionName: "Delete-Supplier List",
      icon: faTrash,
    },
  ];

  const emptyConfig = {
    title: "No Supplier Found",
    description: "",
  };

  const formConfig: FormConfig = {
    controls: [
      {
        name: "name",
        label: "Name",
        placeholder: "Pelucchi",
        type: "text",
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
      {
        name: "address",
        label: "Address",
        placeholder: "Start typing address here...",
        type: "formatted-textarea",
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
    ],
  };

  const formConfigForContactsObject = {
    controls: [
      {
        fieldArray: {
          fieldArrayConfig: {
            fieldArrayName: "motionTime",
          },
          fieldArrayControls: [
            {
              controlGroup: [
                {
                  name: "pointOfContact",
                  label: "POC",
                  type: "text",
                  required: false,
                  readOnly: true,
                  disabled: true,
                },
                {
                  name: "phoneNo",
                  label: "Contact",
                  placeholder: "Enter a contact number (XXXX XXXX)",
                  type: "tel",
                  prefix: "+65",
                  pattern: "[+65]{3}[0-9]{4}[0-9]{4}",
                  required: false,
                  readOnly: isDetails,
                  disabled: isDetails,
                  inputMode: "numeric",
                  maxLength: 8,
                },
              ],
            },
          ],
        },
      },
    ],
  };

  const [formConfigForContacts, setFormConfigForContacts] = useState(
    formConfigForContactsObject
  );

  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .min(2, "Name must be at least 2 characters")
      .max(50, "Name must be at most 50 characters")
      .required("Name is required"),
    address: yup
      .string()
      .required("Address is required")
      .max(1000, "Address should not exceed 1000 characters"),
  });

  const validationSchemaForContacts = yup.object().shape({
    motionTime: yup.array().of(
      yup.object().shape({
        pointOfContact: yup
          .string()
          .required("POC is required")
          .max(50, "POC must be at most 50 characters"),
        phoneNo: yup
          .string()
          .required("Phone number is required")
          .test(
            "is-valid-phone",
            "Invalid phone number format",
            function (value) {
              // Validate phone number format
              return isValidSinglePhoneNumber(value);
            }
          )
          .test(
            "no-duplicate-phone",
            "This phone number is already used",
            function (value, context) {
              // Skip validation if phone number is empty
              if (!value || value.trim() === "") return true;

              // Get the current index from the path
              const currentIndex = /\[(\d+)\]/.exec(context.path ?? "")?.[1];

              // Get all motionTime entries from the root
              const motionTimeEntries = context.from?.[1]?.value?.motionTime || [];

              // Check if current phone number exists at a different index
              const duplicateIndex = motionTimeEntries.findIndex(
                (entry: any, index: number) =>
                  entry.phoneNo?.trim() === value.trim() &&
                  index !== Number(currentIndex)
              );

              return duplicateIndex === -1; // Return true if no duplicate found
            }
          ),
      })
    ),
  });

  const setBlockContactsValues = (details: any) => {
    setValues({
      details: details,
      formGroupObject: formConfigForContactsObject,
      setFormGroupInitialValues: setInitialValuesForContacts,
      setFormConfig: setFormConfigForContacts,
    });
  };

  const onClickAdd = (fieldArrayName?: any) => {
    // Clone the initial form group values to avoid direct mutations
    let copiedInitialValues = _.cloneDeep(initialValuesForContacts);

    // Determine the field array object to be added based on the presence of an index
    let fieldArrayObj =
      copiedInitialValues[fieldArrayName][0] ||
      initialValuesForContactsObjects.motionTime[0];

    // Find the last index of the field array
    const lastIndex = copiedInitialValues[fieldArrayName].length - 1;

    // Set the default values for the new field array object
    Object.keys(fieldArrayObj).forEach((key) => {
      if (key === "pointOfContact") {
        fieldArrayObj[key] = `POC-${lastIndex + 2 < 10 ? "0" : ""}${lastIndex + 2
          }`;
      } else {
        fieldArrayObj[key] = "";
      }
    });

    // Clone the current form values and update them with the new field array object
    let form;

    // Single form
    form = _.cloneDeep(formRefForContacts.current);
    copiedInitialValues = { ...form.values };
    copiedInitialValues[fieldArrayName].push(fieldArrayObj);

    // Update the form group's initial values with the modified values
    setInitialValuesForContacts(copiedInitialValues);

    // Clone the form configuration and update it with the new field array controls
    const copiedFormConfig = _.cloneDeep(formConfigForContacts);

    let controls = copiedFormConfig?.controls; // Single form

    // If controls is undefined and FormConfigObject is provided, use FormConfigObject
    if (
      !controls[0]?.fieldArray?.fieldArrayControls ||
      controls[0]?.fieldArray?.fieldArrayControls?.length === 0
    ) {
      controls = formConfigForContactsObject.controls;
      copiedFormConfig.controls = controls;
      // Update the form configuration with the modified controls
      setFormConfigForContacts(copiedFormConfig);
      return;
    }

    // Find the appropriate control group within the form configuration by the "fieldArrayName"
    for (let obj of controls) {
      if (
        obj?.fieldArray?.fieldArrayConfig?.fieldArrayName === fieldArrayName
      ) {
        // Copy the control group structure from the predefined form configuration
        let newFieldArrayControls = _.cloneDeep(
          obj?.fieldArray?.fieldArrayControls[0]
        );

        // Push the copied control group to the form configuration's controls
        obj?.fieldArray?.fieldArrayControls?.push(newFieldArrayControls);
        break;
      }
    }

    // Update the form configuration with the modified controls
    setFormConfigForContacts(copiedFormConfig);
  };

  const onClickClose = (deletionIdx: any, fieldArrayName: any) => {
    // Single form: Clone the current form values and remove the field
    const form = _.cloneDeep(formRefForContacts.current);

    // Clone the initial form group values to avoid direct mutation
    const copiedInitialValues = { ...form.values };
    copiedInitialValues[fieldArrayName]?.splice(deletionIdx, 1);

    // Get all fieldArray entries
    const fieldArrayEntries = copiedInitialValues[fieldArrayName] || [];
    if (fieldArrayEntries.length > deletionIdx) {
      // Update pointOfContact properties from deletionIdx to the end
      const updatedfieldArrayEntries = fieldArrayEntries.map(
        (entry: any, index: number) => {
          if (index >= deletionIdx) {
            return {
              ...entry,
              pointOfContact: `POC-${index + 1 < 10 ? "0" : ""}${index + 1}`,
            };
          }
          return entry;
        }
      );

      // Create the updated object structure
      const updatedInitialValues = {
        [fieldArrayName]: updatedfieldArrayEntries,
      };

      // Set the updated values
      setInitialValuesForContacts(updatedInitialValues);
    } else {
      setInitialValuesForContacts(copiedInitialValues);
    }

    // Clone the form configuration and update it by removing the field array control
    const copiedFormConfig = _.cloneDeep(formConfigForContacts);

    // Find the appropriate control group within the form configuration by the "fieldArrayName"
    const controls = copiedFormConfig?.controls;

    for (let obj of controls) {
      if (
        obj?.fieldArray?.fieldArrayConfig?.fieldArrayName === fieldArrayName
      ) {
        // Remove the field array control from the form configuration's controls
        obj?.fieldArray?.fieldArrayControls?.splice(deletionIdx, 1);
        break;
      }
    }

    // Update the form configuration with the modified controls
    setFormConfigForContacts(copiedFormConfig);
  };

  let modalHeaderTitle = "Add New Supplier";
  if (isDetails) {
    modalHeaderTitle = "Supplier Details";
  } else if (isDelete) {
    modalHeaderTitle = "Delete Supplier";
  } else if (isEdit) {
    modalHeaderTitle = "Edit Supplier";
  }

  return (
    <div>
      <div>
        <div className="d-flex gap-3 justify-content-end table-generic-action table-toolbar">
          <SearchBarComponent
            placeholder="Search Supplier List"
            methodToDispatch={getSLS}
            getSearch={onSearch}
          />
          <HasPermission
            moduleName="Supplier List"
            actionName="Add-Supplier List"
            isRoute={false}
          >
            <B2Button
              className="icon-text-btn"
              variant="primary"
              onClick={() => setIsModalOpen(true)}
            >
              {/* <FontAwesomeIcon icon={faCirclePlus} className="fs-7 me-2" /> */}
              Add New Supplier
            </B2Button>
          </HasPermission>
        </div>
        <div className="my-3">
          <TableComponent
            tableHeaders={columns}
            emptyConfig={emptyConfig}
            data={data}
            totalNumberOfRecords={totalNumberOfRecords}
            pageSize={pageSize}
            current={current}
            onChangePage={onChangePage}
            actionConfig={actionConfig}
            onChangeSort={onChangeSort}
            isLoadingData={isLoading}
          />
        </div>
      </div>

      {/* modals */}

      {isModalOpen && (
        <ModalComponent
          isConformationModal={isDelete}
          showModal={isModalOpen}
          handleClose={handleCloseModal}
          headerTitle={modalHeaderTitle}
          actionConfig={modalActionConfig}
        >
          {isDelete ? (
            <h5 className="text-center">
              Are you sure to <b className="text-danger">Delete</b>?
            </h5>
          ) : (
            <SkeletonComponent
              wrapperHeight="180px"
              skeletonHeight="50px"
              isLoading={isLoadingSlsDetails}
              count={3}
            >
              <FormBuilderComponent
                formRef={formRef}
                initialValues={initialValues}
                validation={validationSchema}
                formConfig={formConfig}
              />
              {isDetails ? (
                <DynamicFormComponent
                  formRef={formRefForContacts}
                  initialValues={initialValuesForContacts}
                  formConfig={formConfigForContacts}
                  isSecurityMonitoring={true}
                  validation={validationSchemaForContacts}
                  formStyle={{ backgroundColor: "white" }}
                />
              ) : (
                <DynamicFormComponent
                  formRef={formRefForContacts}
                  initialValues={initialValuesForContacts}
                  formConfig={formConfigForContacts}
                  isSecurityMonitoring={true}
                  onClickAddBtn={{ index: 0, func: onClickAdd }}
                  onClickCloseBtn={{ index: 0, func: onClickClose }}
                  validation={validationSchemaForContacts}
                  formStyle={{ backgroundColor: "white" }}
                  addButtonTitle="Add POC"
                  onClickCloseFromStart={true}
                />
              )}
            </SkeletonComponent>
          )}
        </ModalComponent>
      )}
    </div>
  );
};

export default SLSListTableComponent;
